import JsonView from "@uiw/react-json-view";
import { darkTheme } from "@uiw/react-json-view/dark";

export type JsonPreviewProps = { json: string };

/** Shared dark code-block chrome for the JSON viewer / raw fallback. */
const containerStyle = {
  ...darkTheme,
  maxHeight: "70vh",
  overflow: "auto",
  padding: "1rem",
  borderRadius: "0.25rem",
  fontSize: "12px",
} as const;

/**
 * Presentational JSON renderer: a dark, syntax-highlighted code block with
 * collapsible/expandable object & array nodes (initial fold depth 2). Falls back
 * to a raw dark block when the text isn't an object/array or isn't valid JSON.
 */
export const JsonPreview = ({ json }: JsonPreviewProps) => {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return <RawDark>{json}</RawDark>;
  }

  // The tree viewer renders objects/arrays; primitives have nothing to fold.
  if (typeof value !== "object" || value === null) {
    return <RawDark>{JSON.stringify(value, null, 2)}</RawDark>;
  }

  return (
    <JsonView
      value={value as object}
      style={containerStyle}
      collapsed={2}
      displayDataTypes={false}
      enableClipboard
    />
  );
};

/** Raw text in a matching dark code block (parse failure / primitive value). */
function RawDark({ children }: { children: string }) {
  return (
    <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-sm bg-[#202020] p-4 font-mono text-xs text-gray-100">
      {children || "(empty)"}
    </pre>
  );
}
